<template>
  <div class="cart-product">
    <slot name="remove" v-bind="{ removeHandler }">
      <SfCircleIcon
        icon="cross"
        aria-label="Remove"
        class="sf-circle-icon--small cart-product__remove cart-product__remove--circle-icon"
        @click="removeHandler"
      />
      <SfButton
        class="sf-button--text cart-product__remove cart-product__remove--text"
        @click="removeHandler"
        >Remove</SfButton
      >
    </slot>
    <div class="cart-product__aside">
      <slot name="image" v-bind="{ image, title }">
        <SfImage
          :src="image"
          :alt="title"
          :width="imageWidth"
          :height="imageHeight"
          class="cart-product__image"
        />
      </slot>
      <slot name="input">
        <div class="cart-product__quantity-wrapper">
          <SfAddToCart
            :qty="product.qty"
            :product="product"
            :max="showStockErrors ? product.stock : 10000"
            :min="product.isFraction ? 0.01 : 1"
            class="product-grid-item__add-to-cart"
          />
          <!-- <SfQuantitySelector
            :qty="qty"
            :min="minQty"
            :max="showStockErrors ? stock : -1"
            aria-label="Quantity"
            class="cart-product__quantity-selector"
            @input="$emit('input', $event)"
            @hit-max-stock="$emit('hit-max-stock', $event)"
          /> -->
        </div>
      </slot>
    </div>
    <div class="cart-product__main">
      <div class="cart-product__details">
        <slot name="title" v-bind="{ title }">
          <div class="cart-product__title">{{ title }}</div>
          <div
            class="cart-product__variant-name"
            v-text="variantName || ''"
          ></div>
        </slot>
        <div class="cart-product__error" v-text="errorMessage || ''"></div>
        <slot name="price" v-bind="{ specialPrice, regularPrice }">
          <SfPrice
            v-if="regularPrice"
            :regular="regularPrice | price"
            :special="specialPrice | price"
          />
        </slot>
        <slot name="configuration" />
      </div>
      <div class="cart-product__actions">
        <slot name="actions" />
      </div>
    </div>
  </div>
</template>
<script>
import SfPrice from "@lib/components/atoms/SfPrice/SfPrice.vue";
import SfImage from "@lib/components/atoms/SfImage/SfImage.vue";
import SfCircleIcon from "@lib/components/atoms/SfCircleIcon/SfCircleIcon.vue";
import SfButton from "@lib/components/atoms/SfButton/SfButton.vue";
import SfQuantitySelector from "@lib/components/atoms/SfQuantitySelector/SfQuantitySelector.vue";
import SfAddToCart from "@lib/components/molecules/SfAddToCart/SfAddToCart";

export default {
  name: "CartProduct",
  components: {
    SfButton,
    SfImage,
    SfCircleIcon,
    SfPrice,
    SfQuantitySelector,
    SfAddToCart,
  },
  model: {
    prop: "qty",
  },
  props: {
    product: {
      type: Object,
      required: true,
    },
    image: {
      type: [String, Object],
      default: "",
    },
    imageWidth: {
      type: [String, Number],
      default: 100,
    },
    imageHeight: {
      type: [String, Number],
      default: 70,
    },
    /**
     * Product title
     */
    title: {
      type: String,
      default: "",
    },
    variantName: {
      type: String,
      default: "",
    },
    /**
     * Product regular price
     */
    regularPrice: {
      type: [Number, String],
      default: null,
    },
    /**
     * Product special price
     */
    specialPrice: {
      type: [Number, String],
      default: null,
    },
    /**
     * Selected quantity
     */
    qty: {
      type: [Number, String],
      default: 1,
    },
    minQty: {
      type: Number,
      default: 0,
    },
    stock: {
      type: Number,
      default: 0,
    },
    showStockErrors: {
      type: Boolean,
      default: true,
    },
  },
  computed: {
    errorMessage() {
      if (!this.showStockErrors) {
        return null;
      }
      if (this.stock <= 0) {
        return "Out of stock";
      }
      if (this.qty > this.stock) {
        return "Stock not available";
      }
      return null;
    },
  },
  methods: {
    removeHandler() {
      this.$emit("click:remove");
    },
  },
};
</script>
<style lang="scss">
@import "~@lib/styles/helpers";
.cart-product {
  --price-font-size: var(--font-lg);
  position: relative;
  box-sizing: border-box;
  display: flex;
  max-width: var(--collected-product-width);
  padding: var(--collected-product-padding, var(--spacer-xs));
  background: var(--collected-product-background);
  z-index: var(--collected-product-z-index);
  &::after {
    content: "";
  }
  &:hover {
    --collected-product-background: var(--c-white);
  }
  &__remove {
    position: absolute;
    opacity: var(--collected-product-remove-opacity);
    right: var(--collected-product-remove-right, var(--spacer-xs));
    transition: var(--collected-product-remove-transition);
    &--circle-icon {
      top: var(--collected-product-remove-top);
      display: var(--collected-product-remove-circle-icon-display, none);
      transform: var(--collected-product-remove-circle-icon-transform);
    }
    &--text {
      bottom: var(--collected-product-remove-bottom, var(--spacer-xs));
      display: var(--collected-product-remove-text-display);
    }
  }
  &__more-actions {
    --icon-width: 4px;
    --icon-height: 0.9375rem;
    position: absolute;
    top: var(--collected-product-remove-top);
    right: var(--collected-product-remove-right, var(--spacer-xs));
  }
  &__aside {
    position: relative;
    flex: 0 0 100px;
    line-height: 0;
  }
  &__image {
    background: var(--collected-product-image-background, var(--c-light));
  }
  &__quantity-wrapper {
    width: 100px;
    box-sizing: border-box;
    border: 1px solid #ccc;
    --add-to-cart-select-quantity-margin: 0;
    --quantity-selector-width: 100px;
  }
  &__quantity-selector {
    --quantity-selector-background: var(--c-white);
    --quantity-selector-width: 100%;
  }
  &__main {
    flex: 1;
    margin: var(--collected-product-main-margin, 0 0 0 var(--spacer-sm));
    display: flex;
    flex-direction: var(--collected-product-main-flex-direction, column);
  }
  &__details,
  &__actions {
    display: flex;
    flex-direction: column;
  }
  &__title {
    margin-bottom: var(--spacer-2xs);
    @include font(
      --collected-product-title-font,
      var(--font-normal),
      var(--font-lg),
      1.6,
      var(--font-family-secondary)
    );
  }

  .flex-box-horizontal {
    display: flex;
    width: 100%;
    max-width: 18rem;
    justify-content: space-between;
    flex-wrap: wrap;
  }

  &__variant-name {
    margin-bottom: var(--spacer-xs);
    display: inline-block;
    @include font(
      --collected-product-title-font,
      var(--font-normal),
      var(--font-sm),
      1,
      var(--font-family-secondary)
    );

    &:before {
      content: "\200b";
    }
  }

  &__error {
    margin-bottom: var(--spacer-xs);
    color: red;
    display: inline-block;
    @include font(
      --collected-product-title-font,
      var(--font-normal),
      var(--font-sm),
      1,
      var(--font-family-secondary)
    );
  }
}
</style>
